/**
 * TRCE Phase 1 — Consumer app bootstrap from TRCE resolve with legacy fallback.
 */
import type { MutableRefObject } from 'react';
import type { TrceRuntimeThemePack } from '@/lib/trceRuntimeTheme';
import type { TrceFormsSlice } from '@/lib/trceFormTypes';
import type { TrceAiContextSlice, TrceDashboardSlice } from '@/lib/trceAiContextTypes';
import type { TrcePromotionSlice, TrceExperimentSlice, TrceRecommendationSlice } from '@/lib/trcePhase5Types';
import type { TrceWhiteLabelSlice } from '@/lib/trceWhiteLabelTypes';

type Language = 'dari' | 'pashto' | 'english' | 'arabic' | 'uzbek' | 'russian';
type HomepageDisplayMode = 'focused' | 'grid' | 'tabs' | 'scroll_row' | 'sections';

export type SupportedLanguage = {
  code: string;
  appKey: Language;
  name: string;
  native: string;
  isRtl: boolean;
  isDefault: boolean;
};

export type AppModuleShape = {
  id: number;
  name: string;
  displayNameDari: string;
  displayName: string;
  icon: string;
  href: string;
  status: string;
  isVisibleInNav: boolean;
  isHomepageFocus: boolean;
  isSearchable: boolean;
  isPostingEnabled: boolean;
  isCheckoutEnabled: boolean;
  isMarketingEnabled: boolean;
  isPushEnabled: boolean;
  displayPriority: number;
  enabledCities: string[];
};

const LANG_CODE_MAP: Record<string, Language> = {
  fa: 'dari',
  ps: 'pashto',
  en: 'english',
  ar: 'arabic',
  uz: 'uzbek',
  ru: 'russian',
};

const COUNTRY_FALLBACK_LANGUAGES: Record<string, SupportedLanguage[]> = {
  AF: [
    { code: 'fa', appKey: 'dari', name: 'Dari', native: 'دری', isRtl: true, isDefault: true },
    { code: 'ps', appKey: 'pashto', name: 'Pashto', native: 'پښتو', isRtl: true, isDefault: false },
    { code: 'en', appKey: 'english', name: 'English', native: 'English', isRtl: false, isDefault: false },
  ],
  OM: [
    { code: 'ar', appKey: 'arabic', name: 'Arabic', native: 'العربية', isRtl: true, isDefault: true },
    { code: 'en', appKey: 'english', name: 'English', native: 'English', isRtl: false, isDefault: false },
  ],
};

const FALLBACK_CURRENCY: Record<string, { code: string; symbol: string }> = {
  AF: { code: 'AFN', symbol: '؋' },
  OM: { code: 'OMR', symbol: 'ر.ع.' },
  UZ: { code: 'UZS', symbol: "so'm" },
  AE: { code: 'AED', symbol: 'د.إ' },
  PK: { code: 'PKR', symbol: '₨' },
  SA: { code: 'SAR', symbol: 'ر.س' },
  IR: { code: 'IRR', symbol: '﷼' },
};

/** Map TRCE resolve payload → bootstrap-compatible shape (legacy bootstrap preserved). */
export function trceToBootstrapShape(trce: Record<string, unknown>) {
  const ci = (trce.countryIntelligence ?? {}) as Record<string, unknown>;
  const bi = (trce.brandIntelligence ?? {}) as {
    countryBranding?: unknown;
    themePack?: TrceRuntimeThemePack;
  };
  const brand = bi.countryBranding ?? null;
  const geo = (trce.geo ?? {}) as { cities?: unknown[]; provinces?: unknown[] };
  const resolvedFlags = trce.resolvedFlags as { byModule?: Record<string, Record<string, string>> } | undefined;

  return {
    country: {
      id: ci.id,
      name: ci.name,
      nameDari: ci.nameDari,
      code: ci.code,
      defaultLanguage: ci.defaultLanguage,
      defaultCurrency: ci.defaultCurrency,
      currencySymbol: ci.currencySymbol,
      timezone: ci.timezone,
      flagIcon: ci.flagIcon,
      status: ci.status,
    },
    branding: brand,
    settings: ci.settings ?? null,
    languages: ci.languages ?? [],
    cities: geo.cities ?? [],
    provinces: geo.provinces ?? [],
    featureFlags: resolvedFlags?.byModule ?? trce.featureFlags ?? {},
    flagsVersion: trce.flagsVersion,
    paymentMethods: ci.paymentMethods ?? [],
    modules: {
      all: ((trce.modules as { catalog?: unknown[] })?.catalog ?? []).map((m: Record<string, unknown>) => ({
        ...m,
        moduleName: m.moduleName ?? m.key,
      })),
    },
    _trce: true,
    _trceVersion: trce.version,
    _themePack: bi.themePack ?? null,
    _forms: (trce.forms as TrceFormsSlice | undefined) ?? null,
    _aiContext: (trce.aiContext as TrceAiContextSlice | undefined) ?? null,
    _dashboard: (trce.dashboard as TrceDashboardSlice | undefined) ?? null,
    _promotions: (trce.promotions as TrcePromotionSlice | undefined) ?? null,
    _experiments: (trce.experiments as TrceExperimentSlice | undefined) ?? null,
    _recommendations: (trce.recommendations as TrceRecommendationSlice | undefined) ?? null,
    _whiteLabel: (trce.whiteLabel as TrceWhiteLabelSlice | undefined) ?? null,
  };
}

export async function fetchCountryRuntime(
  apiBase: string,
  countryCode: string,
): Promise<{
  source: 'trce' | 'bootstrap';
  data: Record<string, unknown>;
  themePack: TrceRuntimeThemePack | null;
  forms: TrceFormsSlice | null;
  aiContext: TrceAiContextSlice | null;
  dashboard: TrceDashboardSlice | null;
  promotions: TrcePromotionSlice | null;
  experiments: TrceExperimentSlice | null;
  recommendations: TrceRecommendationSlice | null;
  whiteLabel: TrceWhiteLabelSlice | null;
}> {
  try {
    const trceRes = await fetch(`${apiBase}/v1/runtime/resolve?country=${encodeURIComponent(countryCode)}&persona=consumer`);
    if (trceRes.ok) {
      const trce = await trceRes.json() as Record<string, unknown>;
      if (trce.engine === 'TRCE') {
        const shaped = trceToBootstrapShape(trce) as Record<string, unknown>;
        return {
          source: 'trce',
          data: shaped,
          themePack: (shaped._themePack as TrceRuntimeThemePack | null) ?? null,
          forms: (shaped._forms as TrceFormsSlice | null) ?? null,
          aiContext: (shaped._aiContext as TrceAiContextSlice | null) ?? null,
          dashboard: (shaped._dashboard as TrceDashboardSlice | null) ?? null,
          promotions: (shaped._promotions as TrcePromotionSlice | null) ?? null,
          experiments: (shaped._experiments as TrceExperimentSlice | null) ?? null,
          recommendations: (shaped._recommendations as TrceRecommendationSlice | null) ?? null,
          whiteLabel: (shaped._whiteLabel as TrceWhiteLabelSlice | null) ?? null,
        };
      }
    }
  } catch {
    /* fall through to legacy bootstrap */
  }

  const bootRes = await fetch(`${apiBase}/countries/${encodeURIComponent(countryCode)}/bootstrap`);
  if (!bootRes.ok) throw new Error(`bootstrap HTTP ${bootRes.status}`);
  const data = await bootRes.json() as Record<string, unknown>;
  return { source: 'bootstrap', data, themePack: null, forms: null, aiContext: null, dashboard: null, promotions: null, experiments: null, recommendations: null, whiteLabel: null };
}

export type CountryRuntimeApplyHandlers = {
  setHomepageDisplayMode: (mode: HomepageDisplayMode) => void;
  setCountryFlag: (v: string) => void;
  setCountryName: (v: string) => void;
  setCurrencyCode: (v: string) => void;
  setCurrencySymbol: (v: string) => void;
  setCountryBranding: (v: { primaryColor?: string; appName?: string; appNameEn?: string; flagIcon?: string } | null) => void;
  setSupportedLanguages: (v: SupportedLanguage[]) => void;
  setLanguageState: (v: Language) => void;
  setModules: (v: AppModuleShape[]) => void;
  setPaymentMethods: (v: unknown[]) => void;
  setModuleFeatureFlags: (v: Record<string, Record<string, string>>) => void;
  setCountryCities: (v: unknown[]) => void;
  setCity: (v: string) => void;
  setSelectedCityId: (v: number | null) => void;
};

/** Apply bootstrap- or TRCE-normalized payload to AppContext state. */
export function applyCountryRuntimePayload(
  data: Record<string, unknown>,
  selectedCountryCode: string,
  handlers: CountryRuntimeApplyHandlers,
  initialLangSet: MutableRefObject<boolean>,
): void {
  const mode = (data.settings as { homepageDisplayMode?: string } | null)?.homepageDisplayMode;
  if (mode && ['focused', 'grid', 'tabs', 'scroll_row', 'sections'].includes(mode)) {
    handlers.setHomepageDisplayMode(mode as HomepageDisplayMode);
  }

  const c = data.country as Record<string, unknown> | undefined;
  if (c) {
    handlers.setCountryFlag(String(c.flagIcon ?? '🏳'));
    handlers.setCountryName(String(c.nameDari ?? c.name ?? selectedCountryCode));
    const dbCurrency = String(c.defaultCurrency ?? '').toUpperCase();
    const dbSymbol = c.currencySymbol != null ? String(c.currencySymbol) : undefined;
    const fb = FALLBACK_CURRENCY[selectedCountryCode];
    handlers.setCurrencyCode(dbCurrency || fb?.code || 'AFN');
    handlers.setCurrencySymbol(dbSymbol || fb?.symbol || '؋');
  }

  const branding = data.branding as Record<string, unknown> | null;
  if (branding) {
    handlers.setCountryBranding({
      primaryColor: branding.primaryColor as string | undefined,
      appName: branding.appName as string | undefined,
      appNameEn: branding.appNameEn as string | undefined,
      flagIcon: c?.flagIcon as string | undefined,
    });
  }

  const rawLangs = (data.languages ?? []) as Array<Record<string, unknown>>;
  let mapped: SupportedLanguage[] = rawLangs
    .filter(l => l.isActive !== false)
    .map(l => ({
      code: String(l.languageCode),
      appKey: LANG_CODE_MAP[String(l.languageCode)] ?? 'english',
      name: String(l.languageName),
      native: String(l.languageNative),
      isRtl: Boolean(l.isRtl ?? false),
      isDefault: Boolean(l.isDefault ?? false),
    }));

  if (mapped.length === 0) {
    mapped = COUNTRY_FALLBACK_LANGUAGES[selectedCountryCode] ?? [
      { code: 'en', appKey: 'english', name: 'English', native: 'English', isRtl: false, isDefault: true },
    ];
  }

  const ALWAYS_INCLUDE: SupportedLanguage[] = [
    { code: 'fa', appKey: 'dari', name: 'Dari', native: 'دری', isRtl: true, isDefault: false },
    { code: 'ps', appKey: 'pashto', name: 'Pashto', native: 'پښتو', isRtl: true, isDefault: false },
    { code: 'en', appKey: 'english', name: 'English', native: 'English', isRtl: false, isDefault: false },
    { code: 'ar', appKey: 'arabic', name: 'Arabic', native: 'العربية', isRtl: true, isDefault: false },
    { code: 'uz', appKey: 'uzbek', name: 'Uzbek', native: "O'zbek", isRtl: false, isDefault: false },
    { code: 'ru', appKey: 'russian', name: 'Russian', native: 'Русский', isRtl: false, isDefault: false },
  ];
  const existingCodes = new Set(mapped.map(l => l.code));
  for (const lang of ALWAYS_INCLUDE) {
    if (!existingCodes.has(lang.code)) mapped.push(lang);
  }
  handlers.setSupportedLanguages(mapped);

  if (!initialLangSet.current) {
    const defaultLang = mapped.find(l => l.isDefault) ?? mapped[0];
    if (defaultLang) {
      handlers.setLanguageState(defaultLang.appKey);
      initialLangSet.current = true;
    }
  }

  const allMods = ((data.modules as { all?: unknown[] })?.all ?? []) as Array<Record<string, unknown>>;
  const countryMods: AppModuleShape[] = allMods.map(m => ({
    id: Number(m.moduleId ?? m.id),
    name: String(m.moduleName ?? m.name ?? ''),
    displayNameDari: String(m.displayNameDari ?? ''),
    displayName: String(m.displayName ?? ''),
    icon: String(m.icon ?? ''),
    href: String(m.href ?? ''),
    status: String(m.status ?? 'hidden') as AppModuleShape['status'],
    isVisibleInNav: Boolean(m.isVisibleInNav ?? false),
    isHomepageFocus: Boolean(m.isHomeFocus ?? false),
    isSearchable: Boolean(m.isSearchEnabled ?? false),
    isPostingEnabled: Boolean(m.isPostingEnabled ?? false),
    isCheckoutEnabled: Boolean(m.isCheckoutEnabled ?? false),
    isMarketingEnabled: Boolean(m.isMarketingEnabled ?? false),
    isPushEnabled: false,
    displayPriority: Number(m.displayPriority ?? 99),
    enabledCities: (m.enabledCities as string[]) ?? [],
  }));
  handlers.setModules(countryMods);

  const rawPm = (data.paymentMethods ?? []) as Array<{ isActive?: boolean }>;
  handlers.setPaymentMethods(rawPm.filter(p => p.isActive !== false));

  const ff = data.featureFlags;
  if (ff && typeof ff === 'object') {
    handlers.setModuleFeatureFlags(ff as Record<string, Record<string, string>>);
  }

  const bootstrapCities = (data.cities ?? []) as Array<{ isActive?: boolean; nameEn?: string; name_en?: string; id?: number }>;
  if (bootstrapCities.length > 0) {
    const active = bootstrapCities.filter(c => c.isActive !== false);
    handlers.setCountryCities(active);
    const first = active[0];
    handlers.setCity(first?.nameEn ?? first?.name_en ?? '');
    handlers.setSelectedCityId(first?.id ?? null);
  }
}
